import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { api } from '../api/client'
import type { LatestReading, StationDetail, StationParameter } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { useChartPins } from '../lib/useChartPins'
import { withProject } from '../lib/projects'
import { explorerChartKey } from '../lib/chartFormat'
import { usePolled } from '../lib/usePolled'
import {
  TimeSeriesChart, axisGroups, isCrowded, seriesStyle,
} from '../components/TimeSeriesChart'
import { ExportPanel } from '../components/ExportPanel'
import { ChartFrame } from '../components/ChartFrame'
import { LATEST_REFRESH_MS, LatestReadings } from '../components/LatestReadings'
import { StationPhoto } from '../components/StationPhoto'
import { InstrumentList } from '../components/InstrumentList'
import { InstrumentPage } from '../components/InstrumentPage'
import {
  ALERTS_REFRESH_MS, type LiveAlerts, StationStatusLink,
} from '../components/LiveStationAlerts'
import { KindNotes } from '../components/KindNotes'
import { StationHeader } from '../components/StationHeader'
import { TimeWindow } from '../components/TimeWindow'
import { instrumentsPath, resolveInstrumentIds } from '../lib/instruments'
import {
  absentMessage, absentNames, displayValue, drawsNothing, emptyWindowMessage, formatCount, truncationMessage,
} from '../lib/format'
import { type WindowValue, windowRange } from '../lib/timeWindow'
import { StationPicker } from '../components/StationPicker'
import { hiddenAt, showAllChannels, useHiddenChannels } from '../lib/hiddenChannels'

/** What to show before the user has chosen anything.
 *
 *  Two series from different axis groups, preferring the parameters the
 *  station exists to measure. Defaulting to "everything" would put a battery
 *  voltage and a lifetime volume counter on the same chart.
 */
const PREFERRED = ['discharge', 'water_depth', 'water_level', 'precipitation',
  'air_temperature']

/** The station whose station-wide latest readings to poll, or null. Only
 *  once its detail has loaded: until then a gateway station looks like one
 *  without instruments, and its /latest is one probe per channel, about
 *  1,200 at Zhinvali, for readings its page polls per sensor anyway. */
export function latestFor(active: string | undefined,
                          detail: { code: string; instruments: unknown[] } | undefined): string | null {
  if (!active || !detail || detail.code !== active) return null
  return detail.instruments.length > 0 ? null : active
}

/** Every channel of the station, by name, for the list to tick. A channel
 *  the reader hid in Sensor Info is still here, with its checkbox, and in the
 *  export when ticked: hiding only leaves it out of what is charted first
 *  (`defaultSelection`). */
export function listedParameters(parameters: StationParameter[]): StationParameter[] {
  return [...parameters].sort((a, b) => a.display_name.localeCompare(b.display_name))
}

/** The export's columns: every ticked channel, hidden in Sensor Info or not. */
export function exportColumnsFor(parameters: StationParameter[], selected: string[], station: string) {
  return parameters.filter((p) => selected.includes(p.code)).map((p) => ({
    station, code: p.code, display_name: p.display_name, unit: p.unit, axis_group: p.axis_group,
  }))
}

/** What is charted before the reader has chosen. A channel hidden in Sensor
 *  Info is not picked, so a station's first chart is the channels the reader
 *  wants to see; it stays in the list to be ticked. */
export function defaultSelection(all: StationParameter[], station: string,
                                 hidden: ReadonlySet<string>): string[] {
  const parameters = all.filter((p) => !hidden.has(`${station}|${p.code}`))
  const chosen: string[] = []
  const groups = new Set<string>()
  for (const code of PREFERRED) {
    const match = parameters.find((p) => p.code === code)
    if (match && !groups.has(match.axis_group)) {
      chosen.push(match.code)
      groups.add(match.axis_group)
    }
    if (chosen.length === 2) return chosen
  }
  for (const parameter of parameters) {
    if (chosen.includes(parameter.code)) continue
    if (groups.has(parameter.axis_group)) continue
    chosen.push(parameter.code)
    groups.add(parameter.axis_group)
    if (chosen.length === 2) break
  }
  return chosen
}

export function Explorer() {
  const { code, instrument } = useParams()
  // Opened from a project's station list: exports are named after it.
  const [searchParams] = useSearchParams()
  const projectParam = searchParams.get('project')
  // That one project, not the whole list: a project the reader is not in
  // answers 404, and the export simply keeps its usual name.
  const projectId = projectParam && /^\d+$/.test(projectParam) ? Number(projectParam) : null
  const project = useAsync(
    () => (projectId ? api.projects.get(projectId) : Promise.resolve(undefined)), [projectId])
  const projectName = project.data?.project.name
  const navigate = useNavigate()

  const stations = useAsync(() => api.stations(), [])
  const active = code ?? stations.data?.[0]?.code

  const detail = useAsync(
    () => (active ? api.station(active) : Promise.resolve(undefined)),
    [active],
  )

  // A gateway station carries instruments; the four MDS stations never do.
  // This alone decides which sidebar and content render below — everything
  // else in this component keeps working exactly as it does today, since a
  // gateway station's own parameter/series machinery is simply not rendered.
  const gateway = (detail.data?.instruments?.length ?? 0) > 0

  const [selected, setSelected] = useState<string[]>([])
  // Channels the reader hid in Sensor Info (lib/hiddenChannels): left out of
  // the first chart only. They stay in the list and in an export.
  const hidden = useHiddenChannels()

  // The time window, station time (lib/timeWindow.ts). Seven days by
  // default, as it always was.
  const [win, setWin] = useState<WindowValue>(() => ({ kind: 'preset', preset: '7d', at: Date.now() }))
  const [zoomed, setZoomed] = useState(false)
  // A gateway station's export is drawn by its instrument page, which owns
  // the columns, into this place in the side panel.
  const [exportSlot, setExportSlot] = useState<HTMLDivElement | null>(null)

  // Polled here, not inside the panels, so one request serves two places:
  // the header's status and the alert notices; the Latest Readings tiles and
  // the sidebar's latest value beside each parameter.
  const alerts = usePolled<LiveAlerts | undefined>(
    () => (active ? api.stationAlerts(active) : Promise.resolve(undefined)),
    [active], ALERTS_REFRESH_MS)
  // The station's open alarms (phase 4), for the header's pill and list.
  const stationAlarms = usePolled(
    () => (active ? api.alerts.alarms({ station: active }) : Promise.resolve(undefined)),
    [active], ALERTS_REFRESH_MS)
  const latestCode = latestFor(active, detail.data)
  const latest = usePolled<LatestReading[] | undefined>(
    () => (latestCode ? api.latest(latestCode) : Promise.resolve(undefined)),
    [latestCode], LATEST_REFRESH_MS)

  // Re-seed when the station changes: parameter codes are per station, and
  // carrying a selection across would silently show fewer series than the
  // checkboxes claim.
  useEffect(() => {
    if (detail.data) setSelected(defaultSelection(detail.data.parameters, detail.data.code, hidden))
    // Showing or hiding a channel is not a new station: the reader's ticks stay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data])

  const range = useMemo(() => windowRange(win), [win])

  const series = useAsync(
    () =>
      active && selected.length && range.valid && !gateway
        ? api.series({
            stations: [active], parameters: selected,
            from: range.from, to: range.to,
          })
        : Promise.resolve(undefined),
    [active, selected.join(','), range.from.getTime(), range.to.getTime(),
     range.valid, gateway],
  )
  // The chart's pins: for the window, never the zoom, and only the ticked
  // parameters (a gateway station's charts get theirs from InstrumentPage).
  const pinning = useChartPins(gateway ? undefined : active, range.from, range.to, selected,
                               range.valid)

  // Above the early returns below, or the hook count changes between renders
  // and React unmounts the page. Sourced from `detail.data` rather than the
  // sorted `parameters` further down for the same reason — that is computed
  // after them. Order does not matter here: `exportHeaders` sorts into the
  // backend's own column order.
  const exportColumns = useMemo(
    () => exportColumnsFor(detail.data?.parameters ?? [], selected, active ?? ''),
    [detail.data, selected, active],
  )

  if (stations.loading) return <p className="pad muted">Loading…</p>
  if (stations.error) return <p className="pad error">{stations.error}</p>

  // Called directly rather than as JSX: the sidebar and content halves it
  // returns land in two different parents below, and a component can only
  // render into one place.
  const gatewayView = gateway && detail.data && active
    ? buildGatewayView({ detail: detail.data, active, alerts: alerts.data, instrument,
                        range, navigate, exportSlot, onZoomChange: setZoomed, projectName,
                        projectParam })
    : null

  // Ordered by name, since the sidebar no longer groups them.
  const parameters = listedParameters(detail.data?.parameters ?? [])
  const hiddenHere = detail.data ? hiddenAt(hidden, detail.data.code) : 0
  const points = series.data?.series.reduce((n, s) => n + s.points.length, 0) ?? 0
  // Requested, returned, and empty — a channel whose record starts after the
  // window the point limit allowed.
  const absent = absentNames(series.data?.series ?? [])
  // Nothing to draw in this window: the sentence, and no chart.
  const nothing = series.data ? drawsNothing(series.data.series) : false
  const allSelected = parameters.length > 0
    && selected.length === parameters.length


  // The station as the header reads it, with the polled alerts, so its
  // status does not go stale while the page is open.
  const headed = detail.data
    ? { ...detail.data, alerts: alerts.data?.alerts ?? detail.data.alerts,
        health_checked_at: alerts.data ? alerts.data.health_checked_at : detail.data.health_checked_at }
    : null
  const picker = (
    <StationPicker stations={stations.data ?? []} value={active ?? null}
                   onSelect={(code) => navigate(`/explore/${code}`)} />
  )
  // Each charted parameter's colour, by its place among the series drawn,
  // so the sidebar's swatch matches its line.
  const colourOf = new Map((series.data?.series ?? [])
    .map((s, index) => [s.parameter, seriesStyle(index).colour] as const))
  const latestOf = new Map((latest.data ?? []).map((r) => [r.parameter_code, r]))
  const chartTitle = parameters.filter((p) => selected.includes(p.code))
    .map((p) => p.display_name).join(', ')

  return (
    <div className="explore-page">
      {headed
        ? <StationHeader station={headed} picker={picker} alarms={stationAlarms.data?.alarms} />
        : <div className="station-band"><div className="station-band-picker">{picker}</div></div>}

      <div className="explorer">
        <aside className="sidebar">
          <TimeWindow value={win} onChange={setWin} zoomed={zoomed} />

          {/* Between the time window and the list, as both boards draw it:
              the window says what the export covers. Its column list folds
              (ExportPanel) so a long selection does not push the list down. */}
          {gatewayView && <div ref={setExportSlot} />}

          {!gatewayView && active && selected.length > 0 && (
            <ExportPanel
              stations={[active]}
              parameters={selected}
              columns={exportColumns}
              from={range.from}
              to={range.to}
              project={projectName && detail.data
                ? { name: projectName, station: detail.data.name } : undefined}
            />
          )}

          {gatewayView ? gatewayView.sidebar : (
            <div className="field params-field">
              {hiddenHere > 0 && detail.data && <p className="hidden-channels-note" role="status">
                {hiddenHere} not charted first, as you chose in Sensor Info. Still listed and exported.{' '}
                <button type="button" className="link" onClick={() => showAllChannels(detail.data!.code)}>Show all</button></p>}
              <div className="field-head">
                <h2>Parameters</h2>
                {parameters.length > 0 && (
                  <span className="picks">
                    {/* Exporting everything a station records is a normal request
                        and was fourteen clicks. */}
                    <button className="link" disabled={allSelected}
                            onClick={() => setSelected(parameters.map((p) => p.code))}>
                      all
                    </button>
                    <button className="link" disabled={!selected.length}
                            onClick={() => setSelected([])}>
                      none
                    </button>
                  </span>
                )}
              </div>
              {selected.length > 0 && (
                <small className="muted">
                  {selected.length} of {parameters.length} selected
                </small>
              )}
              {detail.loading && <small className="muted">Loading…</small>}
              {/* A flat list. The chart still separates Y axes by axis_group,
                  which is rule 4 and not negotiable, but the picker does not
                  need to mirror it to be usable. */}
              <div className="params">
                {parameters.map((parameter) => {
                  const on = selected.includes(parameter.code)
                  const colour = on ? colourOf.get(parameter.code) : undefined
                  const reading = latestOf.get(parameter.code)
                  const unit = parameter.unit && parameter.unit !== '-' ? parameter.unit : ''
                  return (
                    <label key={parameter.code} className={`check${on ? ' on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => setSelected(toggle(selected, parameter.code))}
                      />
                      {/* The line's own colour, once it is drawn. */}
                      <span className="swatch-line" aria-hidden="true"
                            style={colour ? { background: colour } : undefined} />
                      <span className="check-name">{parameter.display_name}
                        {hidden.has(`${detail.data?.code}|${parameter.code}`) && <small className="muted"> · hidden</small>}</span>
                      <span className="check-value">
                        {reading && (
                          <span className="mono"
                                title={reading.value === null ? undefined : String(reading.value)}>
                            {displayValue(reading.value, reading.precision)}
                          </span>
                        )}
                        {unit && <small className="muted"> {unit}</small>}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )}
        </aside>

        <section className="content">
          {gatewayView ? gatewayView.content : (
            <>
              {active && detail.data && (
                <StationStatusLink code={active} data={alerts.data} />
              )}

              {/* Selecting everything is a normal thing to do before an export, and
                  ten different quantities on one pair of axes is not a chart anyone
                  can read. Say so, and say that the export does not care. */}
              {series.data && !nothing && isCrowded(series.data.series) && (
                <p className="notice notice-warn">
                  {axisGroups(series.data.series).length} different quantities on one
                  chart, readable as a set of shapes, not for reading values off.
                  Narrow the selection to compare properly. The export is
                  unaffected and includes everything ticked.
                </p>
              )}

              {/* A parameter with no readings inside the shared window is not a
                  failure and not an empty chart: it simply has nothing there. It
                  says so, rather than sitting silently in the legend with no line. */}
              {series.data?.truncated && !nothing && absent.length > 0 && (
                <p className="notice notice-warn">
                  {absentMessage(absent, true)}
                </p>
              )}

              <section className="chart-card" aria-labelledby="chart-title">
                <header className="chart-card-head">
                  <h2 id="chart-title">{chartTitle || detail.data?.name || active}</h2>
                  {series.data && (
                    <div className="meta">
                      {/* What the line is, always: every chart here is stored
                          readings, each drawn exactly as collected. Words, not a
                          colour: this is not a status (DESIGN_GUIDELINE §2.6). */}
                      <span className="badge">Stored readings</span>
                      <span className="muted">{formatCount(points)} points</span>
                      {series.data.truncated && series.data.truncated_at && (
                        // Says what period is on screen, not merely that something was
                        // dropped. Every series is cut here, so the chart covers one
                        // window rather than each series covering its own.
                        <span className="pill bad">
                          {truncationMessage(range.from, series.data.truncated_at)}
                        </span>
                      )}
                    </div>
                  )}
                </header>

                {!selected.length ? (
                  <p className="muted">Select a measurement to plot.</p>
                ) : (
                  // No chart while a query is in flight. The old one belongs to the
                  // previous station or range, and the header above it has already
                  // changed to describe the new one.
                  <ChartFrame height={460} loading={series.loading}
                              error={series.error}
                              empty={nothing}
                              emptyMessage={emptyWindowMessage(series.data?.series ?? [])}>
                    {series.data && (
                      <>
                        <TimeSeriesChart series={series.data.series} onZoomChange={setZoomed}
                                         pinning={pinning}
                                         formatKey={active ? explorerChartKey(active) : undefined} />
                        <KindNotes series={series.data.series} />
                      </>
                    )}
                  </ChartFrame>
                )}

              </section>

              {/* What the station is doing now, under what it did over the
                  window above (the board's order). */}
              {active && detail.data ? (
                <LatestReadings state={latest} pollIntervalS={detail.data.poll_interval_s}
                                expectedS={detail.data.expected_interval_s}
                                silentAfterS={detail.data.silent_after_s}
                                eventDriven={new Set(detail.data.parameters
                                  .filter((p) => p.event_driven).map((p) => p.code))} />
              ) : null}

              {/* The logger's camera, on its second data channel (MRL push
                  stations only): what the river looks like now, beside what it
                  measures now. */}
              {active && detail.data?.vendor === 'mrl_push' ? (
                <StationPhoto code={active} />
              ) : null}
            </>
          )}
        </section>
      </div>
    </div>
  )
}

interface GatewayViewProps {
  detail: StationDetail
  active: string
  alerts: LiveAlerts | undefined
  exportSlot: HTMLElement | null
  onZoomChange: (zoomed: boolean) => void
  projectName?: string
  projectParam: string | null
  instrument: string | undefined
  range: { from: Date; to: Date; valid: boolean }
  navigate: (path: string, options?: { replace?: boolean }) => void
}

/** The gateway branch of the explorer: a sensor list with a tick per
 *  instrument, and a page showing every ticked one, instead of the flat
 *  parameter list a CMT station's 700+ channels would otherwise force onto
 *  the MDS-shaped Measurements field (spec §8.4).
 *
 *  The selection lives in the route, `/explore/zhinvali/179469,179476`, so
 *  a link reproduces the page. A tick replaces the history entry rather than
 *  pushing one: Back leaves the page, not the last dozen ticks.
 *
 *  Named and called as a plain function, not a component: its two halves
 *  land in two different parents (`<aside>` and `<section>`) that `Explorer`
 *  itself owns, and a single JSX element cannot render into two places at
 *  once. It runs conditionally — only when `gateway` is true — so it must
 *  stay hook-free: a hook here would violate the rules of hooks the moment a
 *  reader switched from a gateway station to an MDS one, since it would then
 *  simply not run. `instrumentIds` is a pure derivation from already-fetched
 *  data, which is what makes that safe. */
function buildGatewayView({ detail, active, alerts, instrument, range,
                           navigate, exportSlot, onZoomChange, projectName,
                           projectParam }: GatewayViewProps) {
  const instrumentIds = resolveInstrumentIds(detail.instruments, instrument)
  const choose = (ids: number[]) =>
    navigate(withProject(instrumentsPath(active, ids), projectParam), { replace: true })

  const sidebar = (
    <InstrumentList instruments={detail.instruments} selected={instrumentIds}
                    onChange={choose} />
  )
  const content = (
    <>
      {/* Above the sensors: a reader should know the station's alerts — a
          gateway not answering, a silent node — before reading a value. */}
      <StationStatusLink code={active} data={alerts} />
      {detail.instruments.length === 0
        ? <p className="muted">This station has no instruments.</p>
        : (
          <InstrumentPage code={active} instruments={detail.instruments}
                          instrumentIds={instrumentIds}
                          onRemove={(id) => choose(instrumentIds.filter((x) => x !== id))}
                          pollIntervalS={detail.poll_interval_s}
                          expectedS={detail.expected_interval_s}
                          silentAfterS={detail.silent_after_s}
                          from={range.from} to={range.to} valid={range.valid}
                          exportSlot={exportSlot} onZoomChange={onZoomChange}
                          exportProject={projectName
                            ? { name: projectName, station: detail.name } : undefined} />
        )}
    </>
  )
  return { sidebar, content }
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value]
}
